/**
 * Token Encryption Service
 * Encrypts/decrypts tokens stored in localStorage using Web Crypto API
 */

export class TokenEncryptionService {
  private static instance: TokenEncryptionService | null = null;
  private encryptionKey: CryptoKey | null = null;
  private readonly ENCRYPTION_KEY_STORAGE = 'playhub_encryption_key';

  static getInstance(): TokenEncryptionService {
    if (!TokenEncryptionService.instance) {
      TokenEncryptionService.instance = new TokenEncryptionService();
    }
    return TokenEncryptionService.instance;
  }

  /**
   * Initialize encryption key
   */
  async initialize(): Promise<void> {
    try {
      // Try to load existing key
      const storedKey = sessionStorage.getItem(this.ENCRYPTION_KEY_STORAGE);
      
      if (storedKey) {
        // Import existing key
        const keyData = this.base64ToArrayBuffer(storedKey);
        this.encryptionKey = await window.crypto.subtle.importKey(
          'raw',
          keyData,
          { name: 'AES-GCM', length: 256 },
          true,
          ['encrypt', 'decrypt']
        );
      } else {
        // Generate new key
        this.encryptionKey = await window.crypto.subtle.generateKey(
          { name: 'AES-GCM', length: 256 },
          true,
          ['encrypt', 'decrypt']
        );
        
        // Store key in sessionStorage (cleared on browser close)
        const exportedKey = await window.crypto.subtle.exportKey('raw', this.encryptionKey);
        const keyString = this.arrayBufferToBase64(exportedKey);
        sessionStorage.setItem(this.ENCRYPTION_KEY_STORAGE, keyString);
      }
      
      console.log('🔐 Token encryption initialized');
    } catch (error) {
      console.error('Failed to initialize token encryption:', error);
      throw new Error('Token encryption initialization failed');
    }
  }

  /**
   * Encrypt data
   */
  async encrypt(data: string): Promise<string> {
    if (!this.encryptionKey) {
      await this.initialize();
    }

    try {
      // Generate random IV (Initialization Vector)
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      
      // Encode data
      const encoder = new TextEncoder();
      const encodedData = encoder.encode(data);
      
      // Encrypt
      const encryptedData = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        this.encryptionKey!,
        encodedData
      );
      
      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);
      
      // Convert to base64
      return this.arrayBufferToBase64(combined.buffer);
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data
   */
  async decrypt(encryptedData: string): Promise<string> {
    if (!this.encryptionKey) {
      await this.initialize();
    }

    try {
      // Convert from base64
      const combined = this.base64ToArrayBuffer(encryptedData);
      const combinedArray = new Uint8Array(combined);
      
      // Extract IV and encrypted data
      const iv = combinedArray.slice(0, 12);
      const data = combinedArray.slice(12);
      
      // Decrypt
      const decryptedData = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        this.encryptionKey!,
        data
      );
      
      // Decode
      const decoder = new TextDecoder();
      return decoder.decode(decryptedData);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Check if encryption is available
   */
  isAvailable(): boolean {
    return window.crypto && window.crypto.subtle !== undefined;
  }

  /**
   * Clear encryption key (logout)
   */
  clearKey(): void {
    this.encryptionKey = null;
    sessionStorage.removeItem(this.ENCRYPTION_KEY_STORAGE);
    console.log('🔓 Encryption key cleared');
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Convert base64 to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

export default TokenEncryptionService;
